import { MAZE_AUDIO_FILES, MAZE_AUDIO_VOLUME } from '@/constants/audio';
import { getPreloadedAssetUrl } from '@/utils/asset-manifest';
import { Howl, Howler } from 'howler';
import { useCallback, useEffect, useRef } from 'react';

interface MazeAudioController {
  playBgm: () => void;
  stopBgm: () => void;
  playMove: () => void;
  playPickSide: () => void;
  playWin: () => void;
  playLose: () => void;
  unload: () => void;
}

type MazeHowlKey = 'bgm' | 'move' | 'pickSide' | 'win' | 'lose';

type MazeHowls = Record<MazeHowlKey, Howl>;

function warnLoadError(key: keyof typeof MAZE_AUDIO_FILES, error: unknown) {
  // Keep runtime resilient even when placeholder assets are missing.
  console.warn(`[maze-audio] Failed to load "${key}" sound`, error);
}

const MAZE_HOWL_KEYS = [
  'bgm',
  'move',
  'pickSide',
  'win',
  'lose',
] as const satisfies readonly MazeHowlKey[];

export function useMazeAudio(soundEnabled = true): MazeAudioController {
  const howlsRef = useRef<MazeHowls | null>(null);
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;

  /** Howls that were audible before we paused for tab/window background (merged across blur + visibility). */
  const pausedWhileUnfocusedRef = useRef<Set<MazeHowlKey> | null>(null);
  /** True while we have applied background silence (master volume 0 and/or suspend). */
  const inBackgroundRef = useRef(false);
  /** Howler master volume before `Howler.volume(0)` while backgrounded (sound on only). */
  const savedMasterVolumeRef = useRef<number | null>(null);
  /** After real user input or window focus, `!document.hasFocus()` is used when `document.hidden` stays false (some embedded browsers). */
  const userEngagedForFocusHeuristicRef = useRef(false);

  useEffect(() => {
    howlsRef.current ??= {
      bgm: new Howl({
        // CDN bgm has no CORS headers — only warmed into the HTTP cache, not blob-swapped.
        src: [MAZE_AUDIO_FILES.bgm],
        loop: true,
        // HTML5 Audio is paused by browsers when the tab is backgrounded; Web Audio can keep running.
        html5: true,
        volume: MAZE_AUDIO_VOLUME.bgm,
        onloaderror: (_, error) => warnLoadError('bgm', error),
      }),
      move: new Howl({
        src: [getPreloadedAssetUrl(MAZE_AUDIO_FILES.move)],
        volume: MAZE_AUDIO_VOLUME.move,
        onloaderror: (_, error) => warnLoadError('move', error),
      }),
      pickSide: new Howl({
        src: [getPreloadedAssetUrl(MAZE_AUDIO_FILES.pickSide)],
        volume: MAZE_AUDIO_VOLUME.pickSide,
        onloaderror: (_, error) => warnLoadError('pickSide', error),
      }),
      win: new Howl({
        src: [getPreloadedAssetUrl(MAZE_AUDIO_FILES.win)],
        volume: MAZE_AUDIO_VOLUME.win,
        onloaderror: (_, error) => warnLoadError('win', error),
      }),
      lose: new Howl({
        src: [getPreloadedAssetUrl(MAZE_AUDIO_FILES.lose)],
        volume: MAZE_AUDIO_VOLUME.lose,
        onloaderror: (_, error) => warnLoadError('lose', error),
      }),
    };

    const mergePausedKeys = (incoming: Set<MazeHowlKey>) => {
      if (incoming.size === 0) {
        return;
      }
      const cur = pausedWhileUnfocusedRef.current ?? new Set<MazeHowlKey>();
      for (const k of incoming) {
        cur.add(k);
      }
      pausedWhileUnfocusedRef.current = cur;
    };

    const markUserEngaged = () => {
      userEngagedForFocusHeuristicRef.current = true;
    };

    const isPageAwayFromUser = (): boolean => {
      if (typeof document === 'undefined') {
        return false;
      }
      if (document.hidden) {
        return true;
      }
      if (!userEngagedForFocusHeuristicRef.current) {
        return false;
      }
      // Some hosts never flip `document.hidden` when the tab loses selection, but
      // `document.hasFocus()` becomes false after the user has interacted with the page.
      return !document.hasFocus();
    };

    const isPageBackWithUser = (): boolean => {
      if (typeof document === 'undefined') {
        return false;
      }
      return !document.hidden && document.hasFocus();
    };

    const enterBackgroundAudio = () => {
      const sounds = howlsRef.current;
      if (!sounds) {
        return;
      }

      if (!inBackgroundRef.current) {
        inBackgroundRef.current = true;
        if (soundEnabledRef.current) {
          savedMasterVolumeRef.current = Number(Howler.volume());
          Howler.volume(0);
        }
      }

      const wasPlaying = new Set<MazeHowlKey>();
      for (const key of MAZE_HOWL_KEYS) {
        if (sounds[key].playing()) {
          wasPlaying.add(key);
        }
      }
      for (const key of MAZE_HOWL_KEYS) {
        sounds[key].pause();
      }

      mergePausedKeys(wasPlaying);

      if (Howler.usingWebAudio && Howler.ctx?.state === 'running') {
        void Howler.ctx.suspend();
      }
    };

    const leaveBackgroundAudio = () => {
      if (typeof document === 'undefined' || !isPageBackWithUser()) {
        return;
      }

      if (!inBackgroundRef.current) {
        return;
      }

      const sounds = howlsRef.current;
      if (!sounds) {
        return;
      }

      inBackgroundRef.current = false;

      if (savedMasterVolumeRef.current !== null) {
        Howler.volume(savedMasterVolumeRef.current);
        savedMasterVolumeRef.current = null;
      }

      if (Howler.usingWebAudio && Howler.ctx?.state === 'suspended') {
        void Howler.ctx.resume();
      }

      const keys = pausedWhileUnfocusedRef.current;
      pausedWhileUnfocusedRef.current = null;

      if (!soundEnabledRef.current || !keys?.size) {
        return;
      }

      for (const key of keys) {
        sounds[key].play();
      }
    };

    const onVisibilityChange = () => {
      if (typeof document === 'undefined') {
        return;
      }
      if (document.hidden) {
        enterBackgroundAudio();
      } else if (isPageBackWithUser()) {
        // Visible again — focus may arrive a tick later; leave is also driven by poll/focus.
        leaveBackgroundAudio();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange, true);

    /** If the app runs in a same-origin iframe, the iframe document may stay "visible" when only the parent tab changes. */
    let topDoc: Document | null = null;
    try {
      const w = globalThis.window;
      if (w?.top && w.top !== w && w.top.document) {
        topDoc = w.top.document;
        topDoc.addEventListener('visibilitychange', onVisibilityChange, true);
      }
    } catch {
      // Cross-origin parent: cannot attach.
    }

    const win = globalThis.window;
    const onWindowFocus = () => {
      markUserEngaged();
      leaveBackgroundAudio();
    };
    if (win) {
      win.addEventListener('blur', enterBackgroundAudio);
      win.addEventListener('focus', onWindowFocus);
      win.addEventListener('pagehide', enterBackgroundAudio);
    }

    document.addEventListener('pointerdown', markUserEngaged, true);

    const syncAudioToFocusAndVisibility = () => {
      if (!howlsRef.current) {
        return;
      }
      if (isPageAwayFromUser()) {
        enterBackgroundAudio();
      } else if (isPageBackWithUser()) {
        leaveBackgroundAudio();
      }
    };

    const focusPollId = win?.setInterval(syncAudioToFocusAndVisibility, 250);

    return () => {
      if (typeof focusPollId === 'number') {
        win?.clearInterval(focusPollId);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange, true);
      if (topDoc) {
        topDoc.removeEventListener('visibilitychange', onVisibilityChange, true);
      }
      if (win) {
        win.removeEventListener('blur', enterBackgroundAudio);
        win.removeEventListener('focus', onWindowFocus);
        win.removeEventListener('pagehide', enterBackgroundAudio);
      }
      document.removeEventListener('pointerdown', markUserEngaged, true);
      pausedWhileUnfocusedRef.current = null;
      if (inBackgroundRef.current) {
        inBackgroundRef.current = false;
        if (savedMasterVolumeRef.current !== null) {
          Howler.volume(savedMasterVolumeRef.current);
          savedMasterVolumeRef.current = null;
        }
      }
      const sounds = howlsRef.current;
      if (!sounds) {
        return;
      }
      Object.values(sounds).forEach((sound) => sound.unload());
      howlsRef.current = null;
    };
  }, []);

  useEffect(() => {
    Howler.mute(!soundEnabled);

    if (soundEnabled && Howler.ctx?.state === 'suspended') {
      void Howler.ctx.resume();
    }
  }, [soundEnabled]);

  const playBgm = useCallback(() => {
    if (!soundEnabled) {
      return;
    }
    const bgm = howlsRef.current?.bgm;
    if (!bgm || bgm.playing()) {
      return;
    }
    userEngagedForFocusHeuristicRef.current = true;
    bgm.play();
  }, [soundEnabled]);

  const stopBgm = useCallback(() => {
    if (!howlsRef.current?.bgm?.playing()) {
      return;
    }
    howlsRef.current.bgm.stop();
  }, []);

  const playMove = useCallback(() => {
    if (!soundEnabled) {
      return;
    }
    howlsRef.current?.move.play();
  }, [soundEnabled]);

  const playPickSide = useCallback(() => {
    if (!soundEnabled) {
      return;
    }
    howlsRef.current?.pickSide.play();
  }, [soundEnabled]);

  const playWin = useCallback(() => {
    if (!soundEnabled) {
      return;
    }
    howlsRef.current?.win.play();
  }, [soundEnabled]);

  const playLose = useCallback(() => {
    if (!soundEnabled) {
      return;
    }
    howlsRef.current?.lose.play();
  }, [soundEnabled]);

  const unload = useCallback(() => {
    const sounds = howlsRef.current;
    if (!sounds) {
      return;
    }
    Object.values(sounds).forEach((sound) => sound.unload());
    howlsRef.current = null;
  }, []);

  return {
    playBgm,
    stopBgm,
    playMove,
    playPickSide,
    playWin,
    playLose,
    unload,
  };
}
